import React, { useEffect } from "react";
import { observer, useLocalObservable } from "mobx-react";
import { FullscreenModal } from "../../../components/FullscreenModal/FullscreenModal";
import { Popconfirm, Modal, Button, Switch } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { translateKey as t } from "../../../locales/translation";

import "./CrudFormStyles.scss";

interface CrudFormModalProps {
  controller?: any;
  form: any;
  onSave?: any;
  onClose?: any;
  onAfterClose?: any;
  onFullscreen?: any;
  entity: string;
  itemName?: string;
  activeSwitch?: { propertyAccessor: string; title: string; disabled?: any; onChange?: any };
  extraElementsInHeader?: any;
  containsMap?: boolean;
  bodyStyle?: any;
  size?: "small" | "default" | "large" | "fullscreen";
  canMaximize?: boolean;
  centered?: boolean;
  saveBtnValidation?: any;
}

const CrudFormModal: React.FC<CrudFormModalProps> = observer(
  ({
    controller,
    form,
    onSave,
    onClose,
    onAfterClose,
    onFullscreen,
    entity,
    itemName,
    activeSwitch,
    extraElementsInHeader,
    containsMap,
    bodyStyle,
    size,
    canMaximize,
    centered,
    saveBtnValidation,
  }) => {
    const state = useLocalObservable(() => ({
      showCancelConfirmation: false,
      modalSize: "850px",
      setShowCancelConfirmation(state: boolean) {
        this.showCancelConfirmation = state;
      },
      setModalSize(value: string) {
        this.modalSize = value;
      },
    }));

    useEffect(() => {
      // This code runs after every update (but not the initial render)
      console.log("Component did update - modalSize:", state.modalSize);

      if (size === "small") {
        state.setModalSize("550px");
      }
      if (size === "default") {
        state.setModalSize("850px");
      }
      if (size === "large") {
        window.innerWidth >= 1300 ? state.setModalSize("1200px") : state.setModalSize("90%");
      }
      if (size === "fullscreen") {
        state.setModalSize("100%");
      }
    }, [state.modalSize]);

    window.addEventListener("resize", () => {
      if (size === "large") {
        window.innerWidth >= 1300 ? (state.modalSize = "1200px") : (state.modalSize = "90%");
      }
    });

    const handleSwitchChanges = (value: boolean) => {
      if (activeSwitch.onChange) {
        activeSwitch.onChange(value);
      } else {
        controller[entity][activeSwitch.propertyAccessor] = !controller[entity][activeSwitch.propertyAccessor];
      }
    };

    const handleCancelChanges = () => {
      if (state.showCancelConfirmation) {
        state.setShowCancelConfirmation(false);
      }
      controller.setEntity(entity, {});
      controller.setIsFormModalVisible(false);

      if (onClose) {
        onClose();
      }
    };

    const handleModalClose = () => {
      if (JSON.stringify(controller[entity]) === controller.entitySnapshot) {
        handleCancelChanges();
      } else {
        state.setShowCancelConfirmation(true);
      }
    };

    const handleAfterClose = () => {
      // Pass the callback after the modal is closed
      if (onAfterClose) {
        onAfterClose();
      }
    };

    const handleOnFullscreen = (isFullscreen: boolean) => {
      // Pass the callback when the modal size is set to fullscreen
      if (onFullscreen) {
        onFullscreen(isFullscreen);
      }
    };

    if (!controller.isFormModalVisible && !containsMap) {
      return null;
    }

    if (canMaximize) {
      return (
        <div>
          <FullscreenModal
            title={
              controller.isReadOnly ? (
                <>
                  <h5 style={{ display: "inline-block", marginTop: 5, fontWeight: 600, color: "black" }}>
                    {itemName || t("lblForm", "Form")}
                  </h5>
                  {extraElementsInHeader}
                </>
              ) : (
                <>
                  <h5 style={{ display: "inline-block", marginTop: 5, fontWeight: 600, color: "black" }}>
                    {controller.formMode === "create"
                      ? t("lblCreate", "Create") + " " + itemName || t("lblForm", "Form")
                      : t("lblEdit", "Edit") + " " + itemName || t("lblForm", "Form")}
                  </h5>
                  {extraElementsInHeader}
                </>
              )
            }
            open={controller.isFormModalVisible}
            closable={true}
            maskClosable={false}
            width={state.modalSize}
            centered={centered}
            // bodyStyle={bodyStyle || { paddingTop: "0px", paddingBottom: "16px" }}
            styles={{
              header: {
                // lineHeight: '0px'
              },

              body: bodyStyle || { paddingTop: "0px", paddingBottom: "16px" },
            }}
            afterClose={handleAfterClose}
            onClose={handleModalClose}
            onFullscreen={handleOnFullscreen}
            controller={controller}
            footer={
              <div>
                {activeSwitch && (
                  <div style={{ float: "left", marginTop: "5px" }}>
                    <Switch
                      data-e2e="form-modal-active-switch"
                      checked={controller[entity][activeSwitch.propertyAccessor]}
                      disabled={controller.isReadOnly || activeSwitch.disabled}
                      onChange={handleSwitchChanges}
                    />
                    <span style={{ marginLeft: "5px" }}>{activeSwitch.title}</span>
                  </div>
                )}

                {
                  // Render the popover only if the user modifies one or more fields, editing the payload
                  JSON.stringify(controller[entity]) === controller.entitySnapshot ? (
                    <Button
                      data-e2e="form-modal-cancel-btn"
                      type="primary"
                      ghost
                      key="cancel"
                      onClick={handleCancelChanges}
                    >
                      {t("lblCancel", "Cancel")}
                    </Button>
                  ) : (
                    <Popconfirm
                      placement="topRight"
                      title={t(
                        "lblAreYouSureYouWantToDiscardAllChanges",
                        "Are you sure you want to discard all changes?"
                      )}
                      onConfirm={handleCancelChanges}
                      okText={t("lblYes", "Yes")}
                      cancelText={t("lblNo", "No")}
                    >
                      <Button data-e2e="form-modal-cancel-btn" type="primary" ghost key="cancel">
                        {t("lblCancel", "Cancel")}
                      </Button>
                    </Popconfirm>
                  )
                }

                <Button
                  data-e2e="form-modal-save-btn"
                  type="primary"
                  key="save"
                  disabled={saveBtnValidation || JSON.stringify(controller[entity]) === controller.entitySnapshot}
                  onClick={() => onSave()}
                  loading={controller.saveButtonLoading}
                  style={{ marginLeft: 10 }}
                >
                  {controller.formMode === "create" ? t("lblCreate", "Create") : t("lblSave", "Save")}
                </Button>
              </div>
            }
          >
            {form}
          </FullscreenModal>
          <Modal
            open={state.showCancelConfirmation}
            centered={true}
            width="auto"
            okText={t("lblYes", "Yes")}
            cancelText={t("lblNo", "No")}
            onOk={handleCancelChanges}
            onCancel={() => state.setShowCancelConfirmation(false)}
            closable={false}
          >
            <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "#faad14" }} />

            <span style={{ paddingLeft: 5, color: "#000000A6" }}>
              {t("lblAreYouSureYouWantToDiscardAllChanges", "Are you sure you want to discard all changes?")}
            </span>
          </Modal>
        </div>
      );
    } else {
      return (
        <Modal
          title={
            activeSwitch && (
              <div>
                <Switch
                  data-e2e="form-modal-active-switch"
                  checked={controller[entity][activeSwitch.propertyAccessor]}
                  disabled={controller.isReadOnly || activeSwitch.disabled}
                  onChange={handleSwitchChanges}
                />
                <span style={{ marginLeft: "5px" }}>{activeSwitch.title}</span>
              </div>
            )
          }
          open={controller.isFormModalVisible}
          closable={false}
          maskClosable={false}
          width={state.modalSize}
          centered={size === "fullscreen"}
          afterClose={handleAfterClose}
          // bodyStyle={
          //   size === "fullscreen" && activeSwitch
          //     ? { height: "80vh" }
          //     : size === "fullscreen"
          //     ? { height: "90vh" }
          //     : {}
          // }

          styles={{
            body:
              size === "fullscreen" && activeSwitch
                ? { height: "80vh" }
                : size === "fullscreen"
                ? { height: "90vh" }
                : {},
          }}
          // Render the popover only if the user modifies one or more fields, editing the payload
          footer={
            <div>
              {activeSwitch && (
                <div style={{ display: "inline-block" }}>
                  <Switch
                    data-e2e="form-modal-active-switch"
                    checked={controller[entity][activeSwitch.propertyAccessor]}
                    disabled={controller.isReadOnly || activeSwitch.disabled}
                    onChange={handleSwitchChanges}
                  />
                  <span style={{ marginLeft: "5px" }}>{activeSwitch.title}</span>
                </div>
              )}

              {JSON.stringify(controller[entity]) === controller.entitySnapshot ? (
                <Button
                  data-e2e="form-modal-cancel-btn"
                  type="primary"
                  ghost
                  key="cancel"
                  onClick={handleCancelChanges}
                >
                  {t("lblCancel", "Cancel")}
                </Button>
              ) : (
                <Popconfirm
                  placement="topRight"
                  title={t("lblAreYouSureYouWantToDiscardAllChanges", "Are you sure you want to discard all changes?")}
                  onConfirm={handleCancelChanges}
                  okText={t("lblYes", "Yes")}
                  cancelText={t("lblNo", "No")}
                >
                  <Button data-e2e="form-modal-cancel-btn" type="primary" ghost key="cancel">
                    {t("lblCancel", "Cancel")}
                  </Button>
                </Popconfirm>
              )}

              <Button
                data-e2e="form-modal-save-btn"
                type="primary"
                key="save"
                disabled={saveBtnValidation || JSON.stringify(controller[entity]) === controller.entitySnapshot}
                onClick={() => onSave()}
                loading={controller.saveButtonLoading}
              >
                {controller.formMode === "create" ? t("lblCreate", "Create") : t("lblSave", "Save")}
              </Button>
            </div>
          }
        >
          {form}
        </Modal>
      );
    }
  }
);

export { CrudFormModal };
